/**
 * Cookie-less first-party analytics collector (spec §4).
 *
 * Sends `view` / `leave` / `click` beacons to avmg.it. Reads nothing from the
 * device: no cookie, no storage, no screen size. `fetch` + keepalive rather
 * than sendBeacon (uBlock's default "hyperlink auditing" setting blocks
 * sendBeacon in Chromium). Every call is fire-and-forget and never throws.
 */
import type { SedeConfig } from '../sedi/types'

const ENDPOINT = '/api/landing/w/'
// `recensione` covers both /recensione/<token> and the bare /recensione (normalised to `/recensione/`).
const TOKEN_PAGE = /^\/(share|conferma|collabora|recensione|consenso)\//
const EVENT_NAME = /^[a-z0-9_]{1,48}$/
const DEDUPE_MS = 500

type Utm = { s?: string; m?: string; c?: string }
type Payload = { k: 'view' | 'leave' | 'click'; p: string; r?: string; u?: Utm; l?: string; n?: string; ms?: number }

export function normalizePath(pathname: string): string | null {
  let p = pathname.split('?')[0].split('#')[0]
  if (!p.startsWith('/')) return null
  if (!p.endsWith('/')) p += '/'
  if (TOKEN_PAGE.test(p)) return null
  return p
}

export function extractUtm(search: string): Utm {
  const q = new URLSearchParams(search)
  const out: Utm = {}
  const s = q.get('utm_source'), m = q.get('utm_medium'), c = q.get('utm_campaign')
  if (s) out.s = s.slice(0, 100)
  if (m) out.m = m.slice(0, 100)
  if (c) out.c = c.slice(0, 100)
  return out
}

export function classifyAnchor(a: { href: string; track?: string | null }): string | null {
  if (a.track) return EVENT_NAME.test(a.track) ? a.track : null
  const h = a.href.toLowerCase()
  if (h.startsWith('tel:')) return 'phone'
  if (h.startsWith('mailto:')) return 'email'
  if (h.includes('wa.me/') || h.includes('api.whatsapp.com/')) return 'whatsapp'
  if (h.includes('maps.google.') || h.includes('goo.gl/maps') || h.includes('maps.apple.com') || h.includes('google.com/maps')) return 'maps'
  return null
}

export function shouldCollect(sede: Pick<SedeConfig, 'domains'>, env: { dev: boolean; hostname: string; webdriver: boolean; hasWindow: boolean }): boolean {
  if (!env.hasWindow || env.dev || env.webdriver) return false
  return sede.domains.includes(env.hostname.toLowerCase())
}

// ── runtime state (browser only) ──────────────────────────────────────
let base = ''
let active = false
let currentPath: string | null = null
let visibleSince: number | null = null
let engagedMs = 0
let lastView: { p: string; at: number } | null = null
/** True once the current pageview has sent its `leave` (max one per `view`). */
let left = false

function send(payload: Payload): void {
  try {
    void fetch(base + ENDPOINT, {
      method: 'POST', keepalive: true, credentials: 'omit', mode: 'cors',
      headers: { 'Content-Type': 'text/plain' }, body: JSON.stringify(payload),
    }).catch(() => undefined)
  } catch { /* never surface */ }
}

function flushEngagement(): void {
  if (visibleSince !== null) { engagedMs += Date.now() - visibleSince; visibleSince = Date.now() }
}

function leaveCurrent(): void {
  if (!currentPath || left) return
  flushEngagement()
  send({ k: 'leave', p: currentPath, ms: Math.round(engagedMs) })
  left = true
  engagedMs = 0
}

/** `origin + pathname` of the referrer; undefined when absent, unparsable or same-origin. */
function reducedReferrer(): string | undefined {
  try {
    if (!document.referrer) return undefined
    const u = new URL(document.referrer)
    if (u.origin === window.location.origin) return undefined
    return (u.origin + u.pathname).slice(0, 500)
  } catch {
    return undefined
  }
}

export function trackPageview(pathname: string): void {
  if (!active) return
  const p = normalizePath(pathname)
  if (!p) return
  const now = Date.now()
  if (lastView && lastView.p === p && now - lastView.at < DEDUPE_MS) return
  if (currentPath && currentPath !== p) leaveCurrent()
  const firstLoad = currentPath === null
  currentPath = p
  visibleSince = document.visibilityState === 'visible' ? now : null
  engagedMs = 0
  lastView = { p, at: now }
  const payload: Payload = { k: 'view', p, l: navigator.language?.slice(0, 8) }
  if (firstLoad) {
    const r = reducedReferrer()
    if (r) payload.r = r
    const u = extractUtm(window.location.search)
    if (Object.keys(u).length) payload.u = u
  }
  left = false
  send(payload)
}

export function trackEvent(name: string): void {
  if (!active || !currentPath || !EVENT_NAME.test(name)) return
  send({ k: 'click', p: currentPath, n: name })
}

export function initCollector(sede: SedeConfig): () => void {
  const env = {
    dev: !!import.meta.env.DEV,
    hasWindow: typeof window !== 'undefined' && typeof document !== 'undefined',
    hostname: typeof window !== 'undefined' ? window.location.hostname : '',
    webdriver: typeof navigator !== 'undefined' && navigator.webdriver === true,
  }
  if (!shouldCollect(sede, env)) return () => undefined
  base = (import.meta.env.VITE_API_BASE_URL as string | undefined) || 'https://avmg.it'
  active = true

  const onVisibility = () => {
    if (document.visibilityState === 'hidden') {
      flushEngagement()
      visibleSince = null
    } else {
      visibleSince = Date.now()
    }
  }
  const onPageHide = () => leaveCurrent()
  // A back/forward-cache restore is a genuine re-visit: start a new pageview
  // so every `leave` follows its own `view`.
  const onPageShow = (e: PageTransitionEvent) => {
    if (!e.persisted) return
    currentPath = null
    lastView = null
    trackPageview(window.location.pathname)
  }
  const onClick = (e: MouseEvent) => {
    const el = (e.target as Element | null)?.closest('a[href], [data-track]') as HTMLElement | null
    if (!el) return
    const href = el.getAttribute('href') || ''
    const track = el.closest('[data-track]')?.getAttribute('data-track') ?? null
    const name = classifyAnchor({ href, track })
    if (name) trackEvent(name)
  }
  document.addEventListener('visibilitychange', onVisibility)
  window.addEventListener('pagehide', onPageHide)
  window.addEventListener('pageshow', onPageShow)
  document.addEventListener('click', onClick, { capture: true })
  trackPageview(window.location.pathname)

  return () => {
    document.removeEventListener('visibilitychange', onVisibility)
    window.removeEventListener('pagehide', onPageHide)
    window.removeEventListener('pageshow', onPageShow)
    document.removeEventListener('click', onClick, { capture: true })
    active = false; currentPath = null; lastView = null; left = false
  }
}
